import React, { useRef, useState } from 'react';
import { useConsumptionLog, ConsumptionEntry } from '../hooks/useConsumptionLog';
import { useDrugs } from '../contexts/DrugsContext';
import * as Icons from 'react-icons/fa';
import { Drug } from '../types';

interface HeaderProps {
  title: string;
  subtitle: string;
  className?: string;
}

const Header: React.FC<HeaderProps> = ({ title, subtitle, className }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { entries, clearEntries } = useConsumptionLog();
  const { drugs, resetDrugs, importCustomDrug } = useDrugs();
  const [isImporting, setIsImporting] = useState(false);

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all data? This will clear all entries and custom drugs.')) {
      // Clear session storage
      localStorage.clear();
      // Clear state
      clearEntries();
      resetDrugs();
    }
  };

  const handleExport = () => {
    const sessionData = {
      entries,
      customDrugs: drugs.filter(drug => drug.isCustom)
    };

    // Create more consistent date and time format for all devices
    const now = new Date();
    
    // Format: YYYY-MM-DD_HH-MM
    const dateStr = now.getFullYear() + '-' + 
                   String(now.getMonth() + 1).padStart(2, '0') + '-' + 
                   String(now.getDate()).padStart(2, '0');
    
    const timeStr = String(now.getHours()).padStart(2, '0') + '-' + 
                   String(now.getMinutes()).padStart(2, '0');
    
    // Generate a short hash (5 characters) for uniqueness
    const hashStr = Math.random().toString(36).substring(2, 7);
    
    // Generate filename: buzzkeeper_2023-04-06_15-30_ab13f.json
    const filename = `buzzkeeperapp_${dateStr}_${timeStr}_${hashStr}.json`;

    const blob = new Blob([JSON.stringify(sessionData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || isImporting) return;

    setIsImporting(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const sessionData = JSON.parse(e.target?.result as string);
        
        // Ask for confirmation before importing
        if (!window.confirm('Are you sure you want to import this data? This will replace your current session.')) {
          setIsImporting(false);
          // Reset the file input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          return;
        }
        
        // Clear existing data first
        localStorage.clear();
        clearEntries();
        resetDrugs();
        
        // Small delay to ensure state is updated
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Import custom drugs first - WITH ORIGINAL IDs
        if (sessionData.customDrugs?.length > 0) {
          console.log('Import: Found custom drugs in import data:', sessionData.customDrugs);
          
          // Store in session storage directly first
          localStorage.setItem('custom-drugs', JSON.stringify(sessionData.customDrugs));
          console.log('Import: Stored custom drugs in session storage');
          
          // Add each custom drug to the state with ORIGINAL IDs
          for (const drug of sessionData.customDrugs) {
            console.log('Import: Importing custom drug with original ID:', drug.id);
            importCustomDrug(drug);
          }
        }
        
        // Import entries with their original drug IDs
        if (sessionData.entries?.length > 0) {
          const entries = sessionData.entries.map((entry: ConsumptionEntry) => ({
            ...entry,
            // Convert string timestamps to numbers if needed
            timestamp: typeof entry.timestamp === 'string' ? 
              new Date(entry.timestamp).getTime() : 
              entry.timestamp
          }));
          localStorage.setItem('consumption-entries', JSON.stringify(entries));
        }
        
        // Reload the page to initialize with new data
        window.location.reload();
      } catch (error) {
        console.error('Error importing session data:', error);
        alert('Error importing session data. Please make sure the file is valid.');
        setIsImporting(false);
        // Reset the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };
    reader.readAsText(file);
  };

  return (
    <header className={`bg-gray-800 border-b border-gray-700 py-4 sm:py-6 ${className || ''}`}>
      <div className="container mx-auto px-2 sm:px-4 max-w-3xl">
        <div className="flex flex-col items-start gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              <span className="text-purple-500">
                {React.createElement(Icons.FaFlask)}
              </span>
              {title}
            </h1>
            <p className="text-gray-400 mt-1 text-sm sm:text-base">{subtitle}</p>
          </div>
          
          <div className="flex gap-1.5 sm:gap-2">
            <button
              onClick={handleReset}
              className="min-w-[80px] px-2 sm:px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-500 text-sm flex items-center justify-center gap-1"
              title="Reset all data"
              disabled={isImporting}
            >
              {React.createElement(Icons.FaTrash, { className: "text-xs" })}
              Reset
            </button>
            
            <button
              onClick={handleExport}
              className="min-w-[80px] px-2 sm:px-3 py-1.5 bg-purple-700 text-white rounded hover:bg-purple-600 text-sm flex items-center justify-center gap-1"
              title="Export session data"
              disabled={isImporting}
            >
              {React.createElement(Icons.FaDownload, { className: "text-xs" })}
              Export
            </button>
            
            <label className={`min-w-[80px] px-2 sm:px-3 py-1.5 bg-purple-700 text-white rounded hover:bg-purple-600 text-sm flex items-center justify-center gap-1 cursor-pointer ${isImporting ? 'opacity-50 cursor-not-allowed' : ''}`}>
              {isImporting ? 
                <>
                  {React.createElement(Icons.FaSpinner, { className: "text-xs animate-spin" })}
                  Loading...
                </> : 
                <>
                  {React.createElement(Icons.FaUpload, { className: "text-xs" })}
                  Import
                </>
              }
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImport}
                className="hidden"
                disabled={isImporting}
              />
            </label>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 